import { useRef } from 'react';

import { gsap, useGSAP } from '@/lib/gsap';
import { cn } from '@/lib/utils';

export interface ScrollProgressProps {
  className?: string;
  /** Fixed to the top of the viewport (page progress) vs. inline. */
  fixed?: boolean;
}

/**
 * A slim gold progress bar tied directly to scroll position. This is an
 * informational indicator (not decorative motion), so it stays active under
 * reduced motion — it simply reflects where the user is, with no easing.
 */
export function ScrollProgress({ className, fixed = true }: ScrollProgressProps) {
  const ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (!ref.current) return;
      gsap.set(ref.current, { scaleX: 0, transformOrigin: 'left center' });
      gsap.to(ref.current, {
        scaleX: 1,
        ease: 'none',
        scrollTrigger: { start: 0, end: 'max', scrub: 0.3 },
      });
    },
    { scope: ref },
  );

  return (
    <div
      className={cn(
        'pointer-events-none z-[60] h-0.5 w-full bg-brand-gold',
        fixed && 'fixed inset-x-0 top-0',
        className,
      )}
      ref={ref}
      aria-hidden="true"
    />
  );
}
